import { CobaltActionBar } from '@cae/cobalt-react/components/action-bar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltLink } from '@cae/cobalt-react/components/link';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { useHasPermission } from '@contexts/permissions-provider';
import { InstructorRegulationModal } from '@features/instructors/components/regulation-modal/InstructorRegulationModal';
import { useInstructorsData } from '@features/instructors/contexts/data/useInstructorsData';
import { useManageInstructorsSelection } from '@features/instructors/contexts/manage-instructors-selection/useManageInstructorsSelection';
import { useInstructorsProcessingRequests } from '@features/instructors/contexts/processing-requests/useInstructorsProcessingRequests';
import { useInstructorsUnapprovedData } from '@features/instructors/contexts/unapproved/useInstructorsUnapprovedData';
import * as React from 'react';

import type { ManageInstructorsPanelShellProps } from './ManageInstructorsPanelShell.types';

import { ManageInstructorsPanelContent } from '../manage-instructors-panel-content/ManageInstructorsPanelContent';
import styles from './ManageInstructorsPanelShell.module.css';

const ManageInstructorsPanelShell = React.memo(
  ({
    onClose,
  }: Readonly<ManageInstructorsPanelShellProps>): React.ReactElement => {
    const { selectedIds, hasSelection } = useManageInstructorsSelection();
    const isMobileOrTablet = useMediaQuery(MediaQuery.MobileAndTablet);
    const canManageInstructors = useHasPermission(
      'InstructorApproval_Edit_Org'
    );
    const { data, refetch: refetchUnapproved } = useInstructorsUnapprovedData();
    const { refetch: refetchInstructors } = useInstructorsData();
    const { submitRequests } = useInstructorsProcessingRequests();

    const [regulationsModalOpen, setRegulationsModalOpen] =
      React.useState<boolean>(false);

    const handleRegulationsModalOpen = React.useCallback(
      () => setRegulationsModalOpen(true),
      []
    );

    const handleRegulationsModalClose = React.useCallback(
      () => setRegulationsModalOpen(false),
      []
    );

    const handleSubmit = React.useCallback((): void => {
      submitRequests(selectedIds, data, () => {
        refetchUnapproved();
        refetchInstructors();
      });
      onClose();
    }, [
      selectedIds,
      data,
      submitRequests,
      refetchUnapproved,
      refetchInstructors,
      onClose,
    ]);

    return (
      <>
        <CobaltSidePanel
          isOpen
          onClose={onClose}
          showCloseButton={true}
          size={isMobileOrTablet ? 'large' : 'medium'}
          title="Add instructors"
          actionBar={
            <CobaltActionBar
              position="right"
              primaryButton={
                canManageInstructors && (
                  <CobaltButton disabled={!hasSelection} onClick={handleSubmit}>
                    Submit request
                  </CobaltButton>
                )
              }
              secondaryButton={
                <CobaltButton variant="secondary" onClick={onClose}>
                  Cancel
                </CobaltButton>
              }
              variant="floating"
            />
          }
          description={
            <p className={`${styles.description} body-02`}>
              By submitting an approval, you acknowledge the following{' '}
              <CobaltLink
                variant="primary"
                aria-label="regulations"
                className={styles.regulationsLink}
                onClick={handleRegulationsModalOpen}
              >
                regulations
              </CobaltLink>
              .
            </p>
          }
        >
          <ManageInstructorsPanelContent
            canManageInstructors={canManageInstructors}
          />
        </CobaltSidePanel>
        <InstructorRegulationModal
          isOpen={regulationsModalOpen}
          onClose={handleRegulationsModalClose}
        />
      </>
    );
  }
);

ManageInstructorsPanelShell.displayName = 'ManageInstructorsPanelShell';
export { ManageInstructorsPanelShell };
